import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Table } from "react-bootstrap";
import Select from "react-select";
import { HashLoader } from "react-spinners";
import { toast } from "react-toastify";
import { CustomForm, CustomModal } from "../../components";
import {
  useCreateAthleteMutation,
  useDeleteAthleteMutation,
  useFetchAllSportsQuery,
  useFetchAthletesQuery,
  useUpdateAthleteMutation,
} from "../../store/api/apiSlice";
import { Button as CustomBtn } from "../../ui";

const AthleteManagement = () => {
  const {
    data: athletesData,
    isLoading,
    error,
    refetch,
  } = useFetchAthletesQuery();
  const { data: sportsData } = useFetchAllSportsQuery();
  const [createAthlete] = useCreateAthleteMutation();
  const [updateAthlete] = useUpdateAthleteMutation();
  const [deleteAthlete] = useDeleteAthleteMutation();
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [saveButton, setSaveButton] = useState(false);

  const [isModalOpen, setModalOpen] = useState(false);
  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [viewAthleteDetails, setViewAthleteDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedGender, setSelectedGender] = useState(
    selectedAthlete?.gender || ""
  );
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedSport, setSelectedSport] = useState(null);

  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const emailRef = useRef(null);
  const phoneRef = useRef(null);
  const genderRef = useRef(null);
  const typeRef = useRef(null);
  const currentDesigCodeRef = useRef(null);
  const sourceOfContactRef = useRef(null);

  const baseUrl = process.env.REACT_APP_SERVER_URL || "";
  const secretKey = process.env.REACT_APP_SECRET_KEY || "";

  useEffect(() => {
    const fetchSchools = async () => {
      const xAuthToken = JSON.parse(localStorage.getItem("token"))?.token;

      try {
        const response = await fetch(`${baseUrl}/api/university/get-all`, {
          method: "GET",
          headers: {
            "secret-key": secretKey,
            "x-auth-token": xAuthToken,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setSchools(data.university || []);
        } else {
        }
      } catch (error) {}
    };

    fetchSchools();
  }, [baseUrl, secretKey]);

  const openModal = (athlete = null) => {
    setSelectedAthlete(athlete);
    setIsCreating(!athlete);
    setModalOpen(true);
    setSelectedSchool(athlete?.school || null);
    setSelectedSport(athlete?.sport || null);
  };

  const openViewModal = async (athleteId) => {
    try {
      const response = await fetch(
        `${baseUrl}/api/athletes/get-single/${athleteId}`,
        {
          headers: {
            "secret-key": secretKey,
            "x-auth-token":
              JSON.parse(localStorage.getItem("token"))?.token || "",
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setViewAthleteDetails(data.athlete);
        setViewModalOpen(true);
      } else {
        toast.error("Failed to fetch athlete details.");
      }
    } catch (error) {
      toast.error("Error fetching athlete details.");
    }
  };

  const closeModal = () => {
    setSaveButton(false);
    setModalOpen(false);
    setSelectedAthlete(null);
    setIsCreating(false);
    setSelectedSchool(null);
    setSelectedSport(null);
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setViewAthleteDetails(null);
  };

  const handleInputChange = (e) => {
    setSelectedAthlete((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };
  const [errors, setErrors] = useState({});
  const handleSubmit = async (e) => {
    setSaveButton(true);
    e.preventDefault();
    setErrors({});
    const athleteData = {
      firstName: firstNameRef.current?.value || "",
      lastName: lastNameRef.current?.value || "",
      email: emailRef.current?.value || "",
      phone: phoneRef.current?.value || "",
      school: selectedSchool?.value || "",
      sport: selectedSport?.value || "",
      gender: selectedGender,
      type: typeRef.current?.value || "",
      currentDesigCode: currentDesigCodeRef.current?.value || "",
      sourceOfContact: sourceOfContactRef.current?.value || "",
    };

    try {
      if (isCreating) {
        await createAthlete({ data: athleteData }).unwrap();
        setSaveButton(false);
        toast.success("Athlete created successfully!");
      } else if (selectedAthlete && selectedAthlete._id) {
        await updateAthlete({
          data: athleteData,
          id: selectedAthlete._id,
        }).unwrap();
        setSaveButton(false);
        toast.success("Athlete updated successfully!");
      }
      refetch();
      closeModal();
    } catch (error) {
      toast.error("Error while saving athlete");
      setSaveButton(false);

      const errorMessages = error.data?.error || "Error while saving athlete";
      const cleanedErrorMessages = errorMessages.replace(
        "Athlete validation failed: ",
        ""
      );
      const errorFields = cleanedErrorMessages
        .split(", ")
        .map((field) => field.trim());

      const newErrors = {};
      errorFields.forEach((field) => {
        if (field.includes(": ")) {
          const [key, message] = field.split(": ");
          newErrors[key.trim()] = message
            .replace(/`/g, "")
            .replace("Path ", "")
            .trim();
        } else {
          newErrors[field.trim()] = "An unknown error occurred.";
        }
      });

      setErrors(newErrors);
    }
  };

  const schoolOptions = schools.map((school) => ({
    value: school.name,
    label: school.name,
  }));

  const sportOptions =
    sportsData?.sports?.map((sport) => ({
      value: sport.name,
      label: sport.name,
    })) || [];

  const filteredAthletes =
    athletesData?.athletes?.filter((athlete) =>
      `${athlete.firstName} ${athlete.lastName}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    ) || [];

  const sortedAthletes = filteredAthletes?.sort((a, b) => {
    const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
    const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();

    return sortOrder === "asc"
      ? nameA.localeCompare(nameB)
      : nameB.localeCompare(nameA);
  });
  const paginatedUsers = sortedAthletes.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(sortedAthletes.length / itemsPerPage);

  const handleDeleteAthlete = async (athleteId) => {
    try {
      await deleteAthlete({ id: athleteId }).unwrap();
      toast.success("Athlete deleted successfully");
    } catch (error) {
      toast.error("Failed to delete athlete:", error);
    }
    refetch();
  };
  if (isLoading) {
    return (
      <div className="loading-container">
        <HashLoader color="#00b386" size={70} />
      </div>
    );
  }

  if (error) {
    return <h1>Error loading athletes.</h1>;
  }

  return (
    <>
      <div className="d-flex justify-content-between pb-4 gap-4 pt-4">
        <Button
          variant="outline-secondary"
          onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
        >
          {sortOrder === "asc" ? "Sort Descending" : "Sort Ascending"}
        </Button>
        <Form.Control
          type="text"
          placeholder="Search by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <CustomBtn onClick={() => openModal()}>Create</CustomBtn>
      </div>

      <Table striped bordered hover>
        <thead>
          <tr>
            <th>First Name</th>
            <th>Last Name</th>
            <th>Email</th>
            <th>Phone</th>
            <th>School</th>
            <th>Sport</th>
            <th>Gender</th>
            <th>Type</th>
            <th>Source of Contact</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedUsers?.map((athlete) => (
            <tr
              key={athlete._id}
              onClick={() => openViewModal(athlete._id)}
              style={{ cursor: "pointer" }}
            >
              <td>{athlete.firstName}</td>
              <td>{athlete.lastName}</td>
              <td>{athlete.email}</td>
              <td>{athlete.phone}</td>
              <td>{athlete.school}</td>
              <td>{athlete.sport}</td>
              <td>{athlete.gender}</td>
              <td>{athlete.type}</td>
              <td>{athlete.sourceOfContact}</td>
              <td>
                <div
                  className="admin_Actions_gap"
                >
                  <img
                    className="Update-delete-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      openModal(athlete);
                    }}
                    width="30"
                    height="30"
                    src="https://img.icons8.com/ios-glyphs/30/create-new.png"
                    alt="create-new"
                  />

                  <img
                    className="Update-delete-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteAthlete(athlete._id);
                    }}
                    width="30"
                    height="30"
                    src="https://img.icons8.com/ios-glyphs/30/filled-trash.png"
                    alt="filled-trash"
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <div className="d-flex justify-content-center mt-3">
        {Array.from({ length: totalPages }, (_, index) => (
          <Button
            key={index}
            variant={currentPage === index + 1 ? "primary" : "outline-primary"}
            onClick={() => setCurrentPage(index + 1)}
            className="mx-1"
          >
            {index + 1}
          </Button>
        ))}
      </div>

      {isViewModalOpen && viewAthleteDetails && (
        <CustomModal isOpen={isViewModalOpen} onClose={closeViewModal}>
          <div
           className="atheleteManagement_customModal"
          >
            <h3
             className="atheleteManagement_customModal_title"
            >
              Athlete Details
            </h3>
            <div
             className="atheleteManagement_customModal_Div"
            >
              <p>
                <strong>First Name:</strong>{" "}
                {viewAthleteDetails?.firstName || "N/A"}
              </p>
              <p>
                <strong>Last Name:</strong>{" "}
                {viewAthleteDetails?.lastName || "N/A"}
              </p>
              <p>
                <strong>Email:</strong> {viewAthleteDetails?.email || "N/A"}
              </p>
              <p>
                <strong>Phone:</strong> {viewAthleteDetails?.phone || "N/A"}
              </p>
              <p>
                <strong>School:</strong> {viewAthleteDetails?.school || "N/A"}
              </p>
              <p>
                <strong>Sport:</strong> {viewAthleteDetails?.sport || "N/A"}
              </p>
              <p>
                <strong>Gender:</strong> {viewAthleteDetails?.gender || "N/A"}
              </p>
              <p>
                <strong>Type:</strong> {viewAthleteDetails?.type || "N/A"}
              </p>
              <p>
                <strong>Source of Contact:</strong>{" "}
                {viewAthleteDetails?.sourceOfContact || "N/A"}
              </p>
              <p>
                <strong>Current Designation Code:</strong>{" "}
                {viewAthleteDetails?.currentDesigCode || "N/A"}
              </p>
            </div>
          </div>
        </CustomModal>
      )}

      {isModalOpen && (
        <CustomModal isOpen={isModalOpen} onClose={closeModal}>
          <div
            className="admin_CreateModal"
          >
            <h3>{isCreating ? "Create Athlete" : "Update Athlete"}</h3>
            <CustomForm
              onSubmit={handleSubmit}
              className="admin_CreateModal_submit"
            >
              <Form.Control
                placeholder="First Name"
                ref={firstNameRef}
                defaultValue={selectedAthlete?.firstName}
              />{" "}
              {errors.firstName && (
                <span className="text-danger">{errors.firstName}</span>
              )}
              <Form.Control
                placeholder="Last Name"
                ref={lastNameRef}
                defaultValue={selectedAthlete?.lastName}
              />{" "}
              {errors.lastName && (
                <span className="text-danger">{errors.lastName}</span>
              )}
              <Form.Control
                placeholder="Email"
                ref={emailRef}
                defaultValue={selectedAthlete?.email}
              />
              {errors.email && (
                <span className="text-danger">{errors.email}</span>
              )}
              <Form.Control
                placeholder="Phone"
                ref={phoneRef}
                defaultValue={selectedAthlete?.phone}
              />{" "}
              {errors.phone && (
                <span className="text-danger">{errors.phone}</span>
              )}
              <Select
                options={schoolOptions}
                placeholder="Select School"
                value={selectedSchool}
                onChange={setSelectedSchool}
              />{" "}
              {errors.school && (
                <span className="text-danger">{errors.school}</span>
              )}
              <Select
                options={sportOptions}
                placeholder="Select Sport"
                value={selectedSport}
                onChange={setSelectedSport}
              />
              {errors.sport && (
                <span className="text-danger">{errors.sport}</span>
              )}
              <Form.Select
                placeholder="Gender"
                name="Gender"
                value={selectedGender}
                onChange={(e) => setSelectedGender(e.target.value)}
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="Other">Other</option>
              </Form.Select>
              {errors.gender && (
                <span className="text-danger">{errors.gender}</span>
              )}
              <Form.Control
                placeholder="Type"
                ref={typeRef}
                defaultValue={selectedAthlete?.type}
              />
              {errors.type && (
                <span className="text-danger">{errors.type}</span>
              )}
              <Form.Control
                placeholder="Current Designation Code"
                ref={currentDesigCodeRef}
                defaultValue={selectedAthlete?.currentDesigCode}
              />
              {errors.currentDesigCode && (
                <span className="text-danger">{errors.currentDesigCode}</span>
              )}
              <Form.Control
                placeholder="Source of Contact"
                ref={sourceOfContactRef}
                defaultValue={selectedAthlete?.sourceOfContact}
              />
              {errors.sourceOfContact && (
                <span className="text-danger">{errors.sourceOfContact}</span>
              )}
              <div className="atheleteManagement_CreateModal_button">
                <CustomBtn
                  loading={saveButton}
                  type="submit"
                  variant="primary"
                  style={{ marginTop: "15px" }}
                >
                  {isCreating ? "Create" : "Save"}
                </CustomBtn>
              </div>
            </CustomForm>
          </div>
        </CustomModal>
      )}
    </>
  );
};

export default AthleteManagement;
